import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import "./Reports.css";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";

// Load jsPDF and html2canvas libraries for PDF export
const scriptJSPDF = document.createElement("script");
scriptJSPDF.src =
  "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
document.head.appendChild(scriptJSPDF);

const scriptHtml2canvas = document.createElement("script");
scriptHtml2canvas.src =
  "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
document.head.appendChild(scriptHtml2canvas);

// Load the xlsx library for Excel export
const scriptXLSX = document.createElement("script");
scriptXLSX.src = "https://unpkg.com/xlsx/dist/xlsx.full.min.js";
document.head.appendChild(scriptXLSX);

// const API_URL = process.env.REACT_APP_BACKEND_API_URL;
const { API_URL } = require("../../config");
// const API_URL = "/pharmaapi";

const App = () => {
  const formatCurrency = (value) => {
    if (typeof value !== "number" || isNaN(value)) {
      return "N/A";
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "PHP",
    }).format(value);
  };

  const [activeReport, setActiveReport] = useState("inventorySummary");
  const [reportData, setReportData] = useState({});
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [transactionItems, setTransactionItems] = useState([]);

  // Filter state variables
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedDistributorId, setSelectedDistributorId] = useState("all");
  const [selectedProductId, setSelectedProductId] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("active");
  const [selectedType, setSelectedType] = useState("all");
  const [sorting, setSorting] = useState([]);

  // A function to reset all filters to their default state
  const resetFilters = () => {
    setStartDate("");
    setEndDate("");
    setSelectedDistributorId("all");
    setSelectedProductId("all");
    setSelectedStatus("active");
    setSelectedType("all");
    setSorting([]);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [productsRes, distributorsRes, transactionItemsRes] =
        await Promise.all([
          axios.get(`${API_URL}/products`),
          axios.get(`${API_URL}/distributors`),
          axios.get(`${API_URL}/transactions/transaction-items`),
        ]);

      setProducts(productsRes.data);
      setDistributors(distributorsRes.data);
      setTransactionItems(transactionItemsRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const calculateInventorySummary = () => {
      const totalItems = products.length;
      const totalStock = products.reduce(
        (sum, item) => sum + item.totalStocks,
        0
      );
      const outOfStockItems = products.filter(
        (item) => item.totalStocks === 0
      ).length;
      return { totalItems, totalStock, outOfStockItems };
    };

    const getFilteredTransactionItems = () => {
      let filtered = [...transactionItems];

      if (startDate) {
        const start = new Date(startDate);
        filtered = filtered.filter(
          (item) => new Date(item.transaction.createdAt) >= start
        );
      }
      if (endDate) {
        // const end = new Date(endDate);
        const dayAfterEndDate = new Date(endDate);
        dayAfterEndDate.setDate(dayAfterEndDate.getDate() + 1);
        filtered = filtered.filter(
          (item) => new Date(item.transaction.createdAt) <= dayAfterEndDate
        );
      }
      if (selectedDistributorId !== "all") {
        const productsFromDistributor = products
          .filter((p) => p.distributorId === parseInt(selectedDistributorId))
          .map((p) => p.id);
        filtered = filtered.filter((item) =>
          productsFromDistributor.includes(item.productId)
        );
      }
      if (selectedProductId !== "all") {
        filtered = filtered.filter(
          (item) => item.productId === parseInt(selectedProductId)
        );
      }

      if (selectedStatus === "voided") {
        // If the 'Void' checkbox IS CHECKED, we show ALL items (voided and non-voided).
        // To achieve this, we do NOT apply a status filter here.
        // The 'filtered' array keeps all statuses from the initial transactionItems.
      } else if (selectedStatus === "active") {
        // If the 'Void' checkbox is UNCHECKED (default), we only show NON-VOIDED (active) items.
        filtered = filtered.filter(
          (item) => item.transaction.status !== "voided"
        );
      }

      if (selectedType !== "all") {
        filtered = filtered.filter(
          (item) => item.transaction.type === selectedType
        );
      }

      return filtered;
    };

    const calculateTotalSalesReport = (filteredItems) => {
      const activeItems = filteredItems.filter(
        (item) => item.transaction.status === "active"
      );

      const totalSales = activeItems.reduce((sum, item) => {
        // FIX: Ensure lineTotal is parsed as a number for calculation
        const lineTotal = parseFloat(item.lineTotal) || 0;

        return (
          sum + (item.transaction.type === "refund" ? -lineTotal : lineTotal)
        );
      }, 0);

      const totalUnitsSold = activeItems.reduce(
        (sum, item) =>
          sum +
          (item.transaction.type === "refund" ? -item.quantity : item.quantity),
        0
      );
      const uniqueActiveTransactionIds = new Set(
        activeItems.map((item) => item.transactionId)
      );
      const averageTransactionValue =
        uniqueActiveTransactionIds.size > 0
          ? totalSales / uniqueActiveTransactionIds.size
          : 0;

      return { totalSales, totalUnitsSold, averageTransactionValue };
    };

    const calculateSalesByProduct = (filteredItems) => {
      const salesByProduct = {};
      filteredItems.forEach((item) => {
        if (item.transaction.status === "active") {
          const product = products.find((p) => p.id === item.productId);
          if (product) {
            // FIX: Ensure lineTotal is parsed as a number for calculation
            const lineTotal = parseFloat(item.lineTotal) || 0;

            if (!salesByProduct[product.id]) {
              salesByProduct[product.id] = {
                brandName: product.brandName,
                genericName: product.genericName,
                totalAmount: 0,
                quantitySold: 0,
              };
            }
            salesByProduct[product.id].totalAmount +=
              item.transaction.type === "refund" ? -lineTotal : lineTotal;
            salesByProduct[product.id].quantitySold +=
              item.transaction.type === "refund"
                ? -item.quantity
                : item.quantity;
          }
        }
      });
      return Object.values(salesByProduct).sort(
        (a, b) => b.totalAmount - a.totalAmount
      );
    };

    const calculateSalesByDistributor = (filteredItems) => {
      const salesByDistributor = {};
      filteredItems.forEach((item) => {
        if (item.transaction.status === "active") {
          const product = products.find((p) => p.id === item.productId);
          if (product) {
            // FIX: Ensure lineTotal is parsed as a number for calculation
            const lineTotal = parseFloat(item.lineTotal) || 0;

            const distributor = distributors.find(
              (d) => d.id === product.distributorId
            );
            if (distributor) {
              if (!salesByDistributor[distributor.id]) {
                salesByDistributor[distributor.id] = {
                  companyName: distributor.companyName,
                  totalAmount: 0,
                };
              }
              salesByDistributor[distributor.id].totalAmount +=
                item.transaction.type === "refund" ? -lineTotal : lineTotal;
            }
          }
        }
      });
      return Object.values(salesByDistributor).sort(
        (a, b) => b.totalAmount - a.totalAmount
      );
    };

    const data = {};
    const filteredItems = getFilteredTransactionItems();
    data.inventorySummary = calculateInventorySummary();
    data.totalSalesReport = calculateTotalSalesReport(filteredItems);
    data.salesByProduct = calculateSalesByProduct(filteredItems);
    data.salesByDistributor = calculateSalesByDistributor(filteredItems);
    data.allTransactionItems = filteredItems;
    setReportData(data);
  }, [
    products,
    distributors,
    transactionItems,
    startDate,
    endDate,
    selectedDistributorId,
    selectedProductId,
    selectedStatus,
    selectedType,
  ]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "transactionId",
        header: "Trans. ID",
      },
      {
        accessorKey: "productName",
        header: "Product",
        cell: (info) => {
          const item = info.row.original;
          const product = products.find((p) => p.id === item.productId);
          return product
            ? `${product.brandName} (${product.genericName})`
            : "Unknown";
        },
      },
      {
        accessorKey: "distributorName",
        header: "Distributor",
        cell: (info) => {
          const item = info.row.original;
          const product = products.find((p) => p.id === item.productId);
          const distributor = product
            ? distributors.find((d) => d.id === product.distributorId)
            : null;
          return distributor ? distributor.companyName : "N/A";
        },
      },
      {
        accessorKey: "quantity",
        header: "Quantity",
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: "lineTotal",
        header: "Amount",
        cell: (info) => {
          const item = info.row.original;
          // Use parseFloat to ensure lineTotal is treated as a number.
          // Fallback to 0 if lineTotal is null, undefined, or not a string/number.
          const lineTotal = parseFloat(item.lineTotal) || 0;

          const displayAmount =
            item.transaction.type === "refund" ? -lineTotal : lineTotal;

          return formatCurrency(displayAmount);
        },
        size: 150,
        minSize: 100,
      },
      {
        accessorKey: "transaction.createdAt",
        header: "Date & Time",
        cell: (info) =>
          new Date(info.getValue()).toLocaleString("en-US", {
            year: "numeric",
            month: "numeric",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
            second: "numeric",
            hour12: true,
          }),
      },
      {
        accessorKey: "transaction.status",
        header: "Status",
        cell: (info) => (
          <span
            className={
              info.getValue() === "voided"
                ? "text-red-500 font-bold"
                : "text-green-500"
            }
          >
            {info.getValue()}
          </span>
        ),
      },
      {
        accessorKey: "transaction.type",
        header: "Type",
        cell: (info) => (
          <span
            className={
              info.getValue() === "refund"
                ? "text-red-500 font-bold"
                : "text-blue-500"
            }
          >
            {info.getValue()}
          </span>
        ),
      },
    ],
    [products, distributors]
  );

  const table = useReactTable({
    data: reportData.allTransactionItems || [],
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const handleExportPdf = () => {
    const input = document.getElementById("reports-content-section");
    if (!input) {
      console.error("Reports content section not found.");
      return;
    }

    window
      .html2canvas(input, {
        scale: 2,
      })
      .then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new window.jspdf.jsPDF("p", "mm", "a4");
        const imgWidth = 210;
        const pageHeight = 297;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft > 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        pdf.save(`reports-${activeReport}.pdf`);
      });
  };

  const handleExportExcel = () => {
    if (activeReport !== "allTransactions") {
      alert(
        "Excel export is only available for the 'All Transactions' report."
      );
      return;
    }

    if (
      !reportData.allTransactionItems ||
      reportData.allTransactionItems.length === 0
    ) {
      alert("No data to export.");
      return;
    }

    const exportItems = reportData.allTransactionItems.map((item) => {
      const product = products.find((p) => p.id === item.productId);
      const distributor = product
        ? distributors.find((d) => d.id === product.distributorId)
        : null;

      const productName = product
        ? `${product.brandName} (${product.genericName})`
        : "Unknown";
      const distributorName = distributor ? distributor.companyName : "N/A";

      // Ensure lineTotal is a number for accurate export
      const lineTotal = parseFloat(item.lineTotal) || 0;

      const displayAmount =
        item.transaction.type === "refund" ? -lineTotal : lineTotal;

      const transactionDateTime = new Date(
        item.transaction.createdAt
      ).toLocaleString("en-US", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
        hour12: true,
      });

      return {
        "Trans. ID": item.transactionId,
        Product: productName,
        Distributor: distributorName,
        Quantity: item.quantity,
        Amount: displayAmount,
        "Date & Time": transactionDateTime,
        Status: item.transaction.status,
        Type: item.transaction.type,
      };
    });

    const worksheet = window.XLSX.utils.json_to_sheet(exportItems);
    const workbook = window.XLSX.utils.book_new();
    window.XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "All Transactions"
    );
    window.XLSX.writeFile(workbook, `all-transactions-report.xlsx`);
  };

  const renderReportContent = () => {
    if (loading) {
      return <p className="loading-message">Loading reports...</p>;
    }

    switch (activeReport) {
      case "inventorySummary":
        const summary = reportData.inventorySummary;
        return (
          <div className="summary-cards">
            <div className="report-card">
              <h2 className="card-title">Total Items</h2>
              <p className="card-value total-items">{summary.totalItems}</p>
            </div>
            <div className="report-card">
              <h2 className="card-title">Total Stock</h2>
              <p className="card-value total-stock">{summary.totalStock}</p>
            </div>
            <div className="report-card">
              <h2 className="card-title">Out of Stock</h2>
              <p className="card-value out-of-stock">
                {summary.outOfStockItems}
              </p>
            </div>
          </div>
        );

      case "totalSalesReport":
        const sales = reportData.totalSalesReport;
        return (
          <div className="summary-cards">
            <div className="report-card">
              <h2 className="card-title">Total Sales</h2>
              <p className="card-value total-sales">
                {formatCurrency(sales.totalSales)}
              </p>
            </div>
            <div className="report-card">
              <h2 className="card-title">Total Units Sold</h2>
              <p className="card-value total-units">{sales.totalUnitsSold}</p>
            </div>
            <div className="report-card">
              <h2 className="card-title">Avg. Transaction Value</h2>
              <p className="card-value avg-value">
                {formatCurrency(sales.averageTransactionValue)}
              </p>
            </div>
          </div>
        );

      case "salesByProduct":
        const salesByProduct = reportData.salesByProduct;
        return (
          <div className="table-container">
            <h2 className="table-title">Sales by Product</h2>
            <table className="user-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Total Revenue</th>
                  <th>Quantity Sold</th>
                </tr>
              </thead>
              <tbody>
                {salesByProduct.length > 0 ? (
                  salesByProduct.map((item, index) => (
                    <tr key={index}>
                      <td>
                        {item.brandName} ({item.genericName})
                      </td>
                      <td>{formatCurrency(item.totalAmount)}</td>
                      <td>{item.quantitySold}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="no-data">
                      No sales data found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );

      case "salesByDistributor":
        const salesByDistributor = reportData.salesByDistributor;
        return (
          <div className="table-container">
            <h2 className="table-title">Sales by Distributor</h2>
            <table className="user-table">
              <thead>
                <tr>
                  <th>Distributor</th>
                  <th>Total Revenue</th>
                </tr>
              </thead>
              <tbody>
                {salesByDistributor.length > 0 ? (
                  salesByDistributor.map((item, index) => (
                    <tr key={index}>
                      <td>{item.companyName}</td>
                      <td>{formatCurrency(item.totalAmount)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="2" className="no-data">
                      No sales data found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );

      case "allTransactions":
        return (
          <div className="table-container">
            <h2 className="table-title">All Transaction Items</h2>
            <table className="user-table">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        onClick={header.column.getToggleSortingHandler()}
                        style={{ width: header.column.getSize() }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: " ▲",
                          desc: " ▼",
                        }[header.column.getIsSorted()] ?? null}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={table.getAllColumns().length}
                      className="no-data"
                    >
                      No transactions found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {/* Pagination Controls */}
            {table.getPageCount() > 1 && (
              <div className="pagination">
                <button
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                >
                  {"<<"}
                </button>
                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  {"<"}
                </button>
                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  {">"}
                </button>
                <button
                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                  disabled={!table.getCanNextPage()}
                >
                  {">>"}
                </button>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="settings-container">
      <div className="header-container">
        <h1>Reports</h1>
        <div className="export-buttons">
          <button className="export-button" onClick={handleExportPdf}>
            Export to PDF
          </button>
          <button className="export-button" onClick={handleExportExcel}>
            Export to Excel
          </button>
        </div>
      </div>
      <p>Get key insights into your inventory and sales data.</p>

      {/* Tab Navigation with reset logic */}
      <div className="settings-tabs">
        <button
          onClick={() => {
            resetFilters();
            setActiveReport("inventorySummary");
          }}
          className={`settings-tab-button ${
            activeReport === "inventorySummary" ? "active" : ""
          }`}
        >
          Inventory Summary
        </button>
        <button
          onClick={() => {
            resetFilters();
            setActiveReport("totalSalesReport");
          }}
          className={`settings-tab-button ${
            activeReport === "totalSalesReport" ? "active" : ""
          }`}
        >
          Total Sales
        </button>
        <button
          onClick={() => {
            resetFilters();
            setActiveReport("salesByProduct");
          }}
          className={`settings-tab-button ${
            activeReport === "salesByProduct" ? "active" : ""
          }`}
        >
          Sales by Product
        </button>
        <button
          onClick={() => {
            resetFilters();
            setActiveReport("salesByDistributor");
          }}
          className={`settings-tab-button ${
            activeReport === "salesByDistributor" ? "active" : ""
          }`}
        >
          Sales by Distributor
        </button>
        <button
          onClick={() => {
            resetFilters();
            setActiveReport("allTransactions");
          }}
          className={`settings-tab-button ${
            activeReport === "allTransactions" ? "active" : ""
          }`}
        >
          All Transactions
        </button>
      </div>

      {/* The filter controls are now outside the tab-specific content */}
      <div className="filter-controls">
        <div className="filter-group">
          <label htmlFor="start-date">Start Date:</label>
          <input
            type="date"
            id="start-date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label htmlFor="end-date">End Date:</label>
          <input
            type="date"
            id="end-date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label htmlFor="distributor-filter">Distributor:</label>
          <select
            id="distributor-filter"
            value={selectedDistributorId}
            onChange={(e) => setSelectedDistributorId(e.target.value)}
          >
            <option value="all">All</option>
            {distributors.map((distributor) => (
              <option key={distributor.id} value={distributor.id}>
                {distributor.companyName}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="product-filter">Product:</label>
          <select
            id="product-filter"
            value={selectedProductId}
            onChange={(e) => setSelectedProductId(e.target.value)}
          >
            <option value="all">All</option>
            {products.map((product) => (
              <option key={product.id} value={product.id}>
                {product.brandName} ({product.genericName})
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="type-filter">Type:</label>
          <select
            id="type-filter"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
          >
            <option value="all">All</option>
            <option value="sale">Sale</option>
            <option value="refund">Refund</option>
          </select>
        </div>
        <div className="filter-group">
          &nbsp;
          <div className="filter-checkbox-group">
            <input
              type="checkbox"
              id="status-voided"
              checked={selectedStatus === "voided"}
              onChange={() => {
                setSelectedStatus(
                  selectedStatus === "voided" ? "active" : "voided"
                );
              }}
            />
            <label htmlFor="status-voided">Void</label>
          </div>
        </div>
      </div>

      <div id="reports-content-section" className="reports-content-section">
        {renderReportContent()}
      </div>
    </div>
  );
};

export default App;
